import { Link } from "react-router-dom";
import { ArrowRight, Compass, Target, HeartHandshake, Quote, Award, Sun, Wind, Flame, Waves, MountainSnow, Leaf, MessageCircle } from "lucide-react";
import PageHero from "@/components/PageHero";
import { SITE, STATS, PROMOTER } from "@/data/site";

const VASTU_DIRECTIONS = [
  { icon: Sun,        dir: "East",       body: "Living areas and main entrance oriented east — for sunlight, vitality and welcoming prosperity into the home." },
  { icon: Flame,      dir: "South-East", body: "Kitchen placement in Agni corner — for health, warmth and balanced elemental energy." },
  { icon: MountainSnow, dir: "South-West", body: "Master bedroom in the earth quadrant — for stability, deep rest and grounded decision-making." },
  { icon: Waves,      dir: "North-East", body: "Puja & meditation space in Ishaan — for clarity, learning and higher wisdom." },
  { icon: Wind,       dir: "North-West", body: "Guest rooms and utilities in Vayavya — for movement, hospitality and healthy circulation." },
  { icon: Leaf,       dir: "Landscape",  body: "Plantation, water bodies and courtyards positioned to draw positive prana into every room." },
];

const VALUES = [
  { icon: Compass,      title: "Vastu-Aligned Vision", body: "To be Bengal & Bharat's most trusted Vastu-first real estate & construction house — where every home carries the science of harmony." },
  { icon: Target,       title: "Craft-Led Mission",    body: "Deliver every project with uncompromising quality, transparent cost, on-time handover and Vastu-alignment down to the last switch-plate." },
  { icon: HeartHandshake, title: "Family Values",       body: "Integrity first. Craft always. A family-run firm that treats every buyer's home as if it were our own — planned with Vastu, built with love." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About SkyRise"
        title="Vastu-planned homes. Vastu-aligned lives."
        subtitle="Founded on the belief that a home is not just walls and beams — it is where a family's energy grows. Every SkyRise project is oriented, planned and finished in strict accordance with Vastu Shastra."
        image="/images/home.jpeg"
        crumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
      />

      <section className="py-24 md:py-32 bg-[#F8F7F5]" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow mb-5">Our Story</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-[#0B162C]">
              A house that is <span className="italic text-[#C5A059]">aligned</span> becomes a home that <span className="italic text-[#C5A059]">rises</span>.
            </h2>
          </div>
          <div className="md:col-span-7 text-[17px] leading-[1.9] text-[#5C6B7B] space-y-6">
            <p>
              SkyRise Estate & Construction was founded on a single conviction — that <strong className="text-[#0B162C]">Vastu Shastra is not decoration, it is engineering</strong>.
              An ancient science of orientation, energy flow and material placement that our forefathers used to plan
              temples, palaces and cities. We simply refuse to build any other way.
            </p>
            <p>
              For every SkyRise home, our in-house Vastu consultant works alongside the architect from day one —
              locking the main entrance direction, the puja corner, the kitchen's Agni placement, the master bedroom's
              earth quadrant, water sources, ventilation and even the choice of finishes. Nothing is ornamental. Everything is intentional.
            </p>
            <p>
              The name carries the promise: <em>Sky</em> is aspiration; <em>Rise</em> is the discipline that gets you there.
              Deep-blue for stability. Gold for prosperity. And Vastu as the invisible foundation beneath every square foot we deliver —
              in Ranchi, Bhubaneswar, Vijayawada and every city we serve.
            </p>
          </div>
        </div>
      </section>

      {/* PROMOTER / FOUNDER */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-promoter">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14 items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={PROMOTER.photo}
                alt="SkyRise Founder & Managing Director"
                className="w-full h-[560px] object-cover"
                style={{ objectPosition: "center 20%" }}
                data-testid="promoter-photo"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#C5A059] text-white px-6 py-4 hidden md:block">
                <div className="text-xs uppercase tracking-[0.22em]">Registered</div>
                <div className="font-serif-display text-lg leading-tight" data-testid="promoter-reg-badge">CIN {SITE.regNo}</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">Meet the Promoter</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-[#0B162C]" data-testid="promoter-heading">
              A hands-on founder.
            </h2>
            <p className="mt-4 text-xs uppercase tracking-[0.24em] text-[#C5A059]">{PROMOTER.role}</p>
            <div className="mt-8 flex gap-4">
              <Quote size={26} className="text-[#C5A059] shrink-0"/>
              <p className="text-[19px] leading-[1.75] text-[#1A2530] font-serif-display italic">
                “{PROMOTER.quote}”
              </p>
            </div>
            <ul className="mt-10 space-y-3">
              {PROMOTER.credentials.map(c => (
                <li key={c} className="flex items-start gap-3 text-[15px] text-[#1A2530]">
                  <span className="w-6 h-6 grid place-items-center bg-[#0B162C] text-[#C5A059] shrink-0"><Award size={12}/></span>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* THE VASTU WAY */}
      <section className="py-24 md:py-32 bg-[#0B162C] text-white relative overflow-hidden" data-testid="about-vastu">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(197,160,89,0.3), transparent 40%), radial-gradient(circle at 80% 70%, rgba(197,160,89,0.2), transparent 40%)"}}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="eyebrow mb-5" style={{color:"#C5A059"}}>The Vastu Way</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Nine directions. <span className="italic text-[#C5A059]">One</span> harmony.
            </h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed">
              A quick guide to the Vastu principles our architects follow on every SkyRise project — from
              a single-family villa to a multi-storey residential tower.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {VASTU_DIRECTIONS.map((v, i) => (
              <div key={v.dir} className="bg-[#0B162C] p-8 md:p-10 flex flex-col gap-5 hover:bg-[#0F1E3A] transition-colors" data-testid={`vastu-${i}`}>
                <div className="w-12 h-12 grid place-items-center bg-[#C5A059] text-[#0B162C]"><v.icon size={20}/></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059] mb-2">Direction 0{i+1}</div>
                  <h3 className="font-serif-display text-2xl md:text-3xl">{v.dir}</h3>
                </div>
                <p className="text-[15px] leading-relaxed text-white/70">{v.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-4">
            <a href={SITE.siteVisitHref("Vastu Consultation")} target="_blank" rel="noreferrer" className="btn-gold" data-testid="vastu-cta-consult">
              <MessageCircle size={16}/> Book a Vastu Consultation
            </a>
            <Link to="/services#interior" className="btn-outline-gold" data-testid="vastu-cta-services">
              See Vastu Interior Service <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F7F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <div key={v.title} className="border border-[#E5E7EB] p-10 flex flex-col gap-5 hover:border-[#C5A059] transition-colors bg-white" data-testid={`value-${i}`}>
                <div className="w-12 h-12 grid place-items-center bg-[#0B162C] text-[#C5A059]"><v.icon size={20}/></div>
                <h3 className="font-serif-display text-3xl text-[#0B162C]">{v.title}</h3>
                <p className="text-[15px] leading-relaxed text-[#5C6B7B]">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-[#0B162C] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" style={{color:"#C5A059"}}>By the Numbers</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {STATS.map(s => (
              <div key={s.label} className="border-t border-white/15 pt-6" data-testid={`about-stat-${s.label.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="font-serif-display text-5xl md:text-6xl text-[#C5A059]">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.24em] text-white/70 mt-3">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F7F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="eyebrow mb-4">Ready to Build?</div>
            <h3 className="font-serif-display text-4xl md:text-5xl leading-[1.05] max-w-2xl text-[#0B162C]">
              Let’s plan your next Vastu-aligned home — together.
            </h3>
          </div>
          <div className="flex gap-4">
            <Link to="/projects" className="btn-outline-navy" data-testid="about-cta-projects">See Our Work <ArrowRight size={16}/></Link>
            <a href={SITE.telHref} className="btn-gold" data-testid="about-cta-call">Call {SITE.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
